import { PiCaretDown } from 'react-icons/pi'
import { REACT_SELECT_DEFAULT_CONTROL_HEIGHT } from 'components/FormUtils/ReactSelectWrapper'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'
import { FilterOption, MultiSelectList } from './MultiSelectList'

export type { FilterOption }

/**
 * Sidebar multi-select for a filter's values: a labelled trigger showing the
 * current selection (Any / All / N selected) over a popover holding the shared
 * MultiSelectList, plus a clearly labelled Remove tied to this filter. Used by
 * the builder's ReportControls; the quick-bar pill uses MultiSelectList directly.
 */
export function FilterSelect({
  label,
  options,
  selected,
  onChange,
  onRemove,
  idPrefix,
}: {
  label: string
  options: FilterOption[]
  selected: string[]
  onChange: (next: string[]) => void
  /** When set, shows a Remove action that clearly belongs to this filter. */
  onRemove?: () => void
  idPrefix: string
}) {
  const summary =
    selected.length === 0
      ? 'Any'
      : selected.length === options.length
        ? 'All'
        : `${selected.length} selected`

  return (
    <div className="flex min-w-44 flex-col">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs text-fg-secondary">{label}</span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-fg-muted hover:text-error"
          >
            Remove
          </button>
        )}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            style={{ minHeight: REACT_SELECT_DEFAULT_CONTROL_HEIGHT }}
            className="form-control-select flex w-full items-center bg-surface focus:outline-none"
          >
            <span
              className={cn(
                'flex-1 truncate px-2 text-left text-sm',
                selected.length === 0
                  ? 'form-control-select-placeholder'
                  : 'text-fg'
              )}
            >
              {summary}
            </span>
            <span className="px-2 text-fg-muted">
              <PiCaretDown size={16} />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          <MultiSelectList
            label={label}
            options={options}
            selected={selected}
            onChange={onChange}
            idPrefix={idPrefix}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
